import { ConsultantWidgetDesktop, VisibilityWrapper } from '@cian/newbuilding-chat-component';
import * as React from 'react';
import { useSelector, useDispatch } from 'react-redux';

import {
  trackShowButton,
  trackClickChatOpen,
  trackClickChatClose,
  trackShowOnCommonFilter,
  trackOpenedOnCommonFilter,
} from './analytics';
import { prepareUserData } from './helpers';
import { prepareMessangers } from './helpers/prepareMessangers';
import { userSelector } from './selectors';
import * as styles from './styles.css';
import { CHAT_FORCE_OPEN_KEY, MOTIVATION_POPUP_KEY } from '../../../constants/newbuildingConsultant';
import { configSelector } from '../../../selectors/configSelector';
import {
  chatAvailableSelector,
  isCommonFilterSelector,
  chatOpenSelector,
  isForceNeedOpenSelector,
  motivationPopupAvailableSelector,
} from '../../../selectors/newbuildingConsultant';
import { newbuildingChatOpenToggle, newbuildingOpenMotivationToggle } from '../../../serp/state/newbuildingConsultant';
import { TThunkDispatch } from '../../../types/redux';
import { isNeedToShow } from '../../../utils/newbuildingConsultant/periodicActions';
import { MotivationConsultantPopupContainer } from '../MotivationConsultantPopup';
import { useWebimChatAutoClosing } from 'shared/hooks/webim';

export function NewbuildingChatContainer() {
  const [ready, setReady] = React.useState(false);

  const dispatch = useDispatch<TThunkDispatch>();
  const config = useSelector(configSelector);
  const accountName = config.get<string>('webim.accountName');
  const host = config.get<string>('webim.chat.host');
  const chatOpenDelay = config.get<number>('newbuildingChat.chatOpenDelay') || 30000;

  const availableToShow = useSelector(chatAvailableSelector);
  const commonFilter = useSelector(isCommonFilterSelector);
  const user = useSelector(userSelector);
  const isOpen = useSelector(chatOpenSelector);
  const needForceOpen = useSelector(isForceNeedOpenSelector);
  const isMotivationPopupAvailable = useSelector(motivationPopupAvailableSelector);

  const isVisible = isOpen || availableToShow;
  const messengersLinks = React.useMemo(() => prepareMessangers(config), [config]);

  const toggleOpen = React.useCallback(
    (opened: boolean) => {
      dispatch(newbuildingChatOpenToggle(opened));

      if (opened) {
        trackClickChatOpen();
      } else {
        trackClickChatClose();
      }
    },
    [dispatch],
  );
  const onReady = React.useCallback(() => setReady(true), []);

  const intervalRef = React.useRef<ReturnType<typeof setTimeout> | void>();

  React.useEffect(() => {
    if (ready && needForceOpen && availableToShow && isNeedToShow(CHAT_FORCE_OPEN_KEY)) {
      intervalRef.current = setTimeout(() => {
        dispatch(newbuildingChatOpenToggle(true));
      }, chatOpenDelay);
    }

    return () => {
      if (intervalRef.current) {
        intervalRef.current = clearTimeout(intervalRef.current);
      }
    };
  }, [ready, needForceOpen, availableToShow, dispatch, chatOpenDelay]);

  React.useEffect(() => {
    if (ready && isMotivationPopupAvailable && availableToShow && isNeedToShow(MOTIVATION_POPUP_KEY)) {
      dispatch(newbuildingOpenMotivationToggle(true));
    }
  }, [ready, isMotivationPopupAvailable, availableToShow, dispatch]);

  React.useEffect(() => {
    if (isOpen && intervalRef.current) {
      intervalRef.current = clearTimeout(intervalRef.current);
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (isVisible) {
      trackShowButton();
    }
  }, [isVisible]);

  React.useEffect(() => {
    if (availableToShow && commonFilter) {
      trackShowOnCommonFilter();
    }
  }, [availableToShow, commonFilter]);

  React.useEffect(() => {
    if (isOpen && commonFilter) {
      trackOpenedOnCommonFilter();
    }
  }, [isOpen, commonFilter]);

  useWebimChatAutoClosing();

  if (!accountName) {
    return null;
  }

  const webimProps = {
    accountName,
    userData: prepareUserData(user),
    host: host || undefined,
  };

  return (
    <VisibilityWrapper visible={isVisible}>
      <ConsultantWidgetDesktop
        webimProps={webimProps}
        chatOpened={isOpen}
        tooltip={<MotivationConsultantPopupContainer />}
        messengersLinks={messengersLinks}
        onReady={onReady}
        toggleOpen={toggleOpen}
        styles={styles['widget-container']}
      />
    </VisibilityWrapper>
  );
}
